/**
 * `billing.*` — the Subscription & billing settings surface.
 *
 * `overview` is the single read-model the page renders: plan + status + trial,
 * plus live usage vs. the plan's limits (seats, storage, WhatsApp + AI credits)
 * and the unlocked modules. No prices (V1 activation is sales-led).
 */
import { z } from "zod";

/** Plan codes on the wire — mirrors the domain `PlanCodeSchema` (contract can't import domain). */
export const planCodeSchema = z.enum(["starter", "growth", "scale", "design_partner"]);

export const overviewInput = z.object({});

export const overviewOutput = z.object({
  plan: z.string(),
  status: z.string(),
  trialEndsOn: z.string().nullable(),
  trialDaysLeft: z.number().nullable(),
  writeEnabled: z.boolean(),
  usage: z.object({
    seats: z.object({ used: z.number().int(), limit: z.number().int() }),
    /** Stringified bigint bytes. */
    storage: z.object({ usedBytes: z.string(), limitBytes: z.string() }),
    whatsapp: z.object({ used: z.number().int(), bundled: z.number().int(), balance: z.number().int() }),
    ai: z.object({ used: z.number().int(), monthlyAllocation: z.number().int(), balance: z.number().int() }),
  }),
  modules: z.array(z.string()),
  /** Installed-and-enabled add-on keys (garment, pos, …) — migration 0106. */
  enabledAddons: z.array(z.string()),
  /**
   * ISO timestamp when the user scheduled cancel-at-period-end from Billing.
   * Null until they do. Drives the "Cancel plan" ↔ "Access ends {date}" flip
   * on the billing hero. @see ADR-0047.
   */
  cancelledAt: z.string().nullable(),
  /**
   * The "access ends on" date (from Razorpay's `current_end`). Powers the
   * confirm-dialog preview and the post-cancel banner. Null for trial-only
   * rows that never activated on Razorpay.
   */
  currentPeriodEndsOn: z.string().nullable(),
});

/**
 * `cancelSubscription` — user-scheduled cancel-at-period-end (ADR-0047).
 * Owner-only (`billing.manage`). The workspace stays fully usable until the
 * current period ends; Razorpay's `subscription.cancelled` webhook then flips
 * status → `canceled` and the entitlement projection turns off write access.
 */
export const cancelSubscriptionInput = z.object({
  /**
   * Required free-text reason (1–500 chars) captured on the confirm dialog.
   * Making this mandatory is a deliberate friction step — the churn signal is
   * worth more than the tiny fraction of users a required field turns away.
   */
  reason: z.string().trim().min(1).max(500),
});
export const cancelSubscriptionOutput = z.object({
  cancelledAt: z.string(),
  currentPeriodEndsOn: z.string().nullable(),
});

/**
 * `listInvoices` — the Billing page: the workspace's payment method on file plus
 * its invoices (Razorpay is the merchant of record, so it issues them). Owner-
 * only (`billing.manage`). `configured: false` → online billing isn't wired in
 * this environment (or no paying subscription yet) → the UI shows an empty state.
 */
export const listInvoicesInput = z.object({});
export const listInvoicesOutput = z.object({
  configured: z.boolean(),
  plan: z.string(),
  status: z.string(),
  /** Payment method on file (`upi` | `card` | `netbanking`), or null. */
  paymentMethod: z.string().nullable(),
  invoices: z.array(
    z.object({
      id: z.string(),
      number: z.string().nullable(),
      /** ISO date the invoice was issued, or null. */
      issuedAt: z.string().nullable(),
      amountPaise: z.number().int(),
      currency: z.string(),
      status: z.string(),
      /** Hosted invoice page for view/download, or null. */
      downloadUrl: z.string().nullable(),
    }),
  ),
});

/**
 * `setAddon` — install or remove an optional add-on module (garment, pos, …) for
 * the workspace. Owner-only (`billing.manage`), write-gated; available on any
 * plan and any (write-enabled) status. The new modules apply immediately.
 */
export const setAddonInput = z.object({ addonKey: z.string(), enabled: z.boolean() });
export const setAddonOutput = z.object({
  addonKey: z.string(),
  enabled: z.boolean(),
  changed: z.boolean(),
});

/**
 * `createTopupOrder` — start a prepaid WhatsApp-credit top-up (§16). Creates a
 * Razorpay one-time order for the chosen pack and returns what the client needs
 * to open Razorpay Checkout. Crediting happens on the payment webhook (not here).
 * Owner-only (`billing.manage`).
 */
export const createTopupOrderInput = z.object({ packId: z.string() });
export const createTopupOrderOutput = z.object({
  orderId: z.string(),
  amountPaise: z.number().int(),
  currency: z.string(),
  credits: z.number().int(),
  razorpayKeyId: z.string(),
});

/**
 * `createSubscription` — start self-serve activation (trial → active). Creates a
 * Razorpay Subscription for the chosen tier and returns what the client needs to
 * open Razorpay Checkout (`subscriptionId`). The activation itself is confirmed
 * server-side on the `subscription.activated` webhook — never the client. Owner-
 * only (`billing.manage`). @see ADR-0023 §3.
 */
export const createSubscriptionInput = z.object({ toPlan: planCodeSchema });
export const createSubscriptionOutput = z.object({
  subscriptionId: z.string(),
  razorpayKeyId: z.string(),
  plan: planCodeSchema,
  /** Monthly price in paise (display reassurance on the checkout). */
  amountPaise: z.number().int(),
});

/**
 * `checkActivation` — poll-safe read-only check: has the subscription activated
 * yet? If the webhook landed, returns immediately. If not, queries Razorpay
 * directly and activates locally when Razorpay reports `active`. Used by the
 * client-side exponential-backoff loop after Checkout closes, and by the nightly
 * reconciler as a last resort. Idempotent, no side-effects beyond the one-time
 * activation. @see ISS-006.
 */
export const checkActivationInput = z.object({});
export const checkActivationOutput = z.object({
  status: z.string(),
  activated: z.boolean(),
});

/**
 * `changePlan` — switch the workspace to a different plan in-app. Owner-only
 * (`billing.manage`) and write-gated. The new limits take effect immediately;
 * a downgrade is rejected if current usage exceeds the target ceiling. For an
 * active, paying subscription the Razorpay subscription's plan is updated too
 * (proration on the next invoice; no refunds — ADR-0023 §3).
 */
export const changePlanInput = z.object({ toPlan: planCodeSchema });

export const changePlanOutput = z.object({
  plan: planCodeSchema,
  fromPlan: planCodeSchema,
  direction: z.enum(["upgrade", "downgrade", "lateral"]),
});

/**
 * `creditTransactions` — the detailed usage statement: a paginated, newest-first
 * view of the credit ledger for a wallet, with the per-row `detail` surfaced
 * from metadata so the UI can show exactly what each credit bought (which model,
 * tokens in/out, cost, or which WhatsApp template) without parsing raw JSON.
 * Cursor = last row id. Read-only.
 */
export const creditWalletSchema = z.enum(["ai", "whatsapp"]);

export const creditEntryKindSchema = z.enum([
  "allocation",
  "top_up",
  "reset",
  "usage",
  "reservation",
  "settlement",
  "release",
  "expiry",
  "adjustment",
]);

/** Typed, optional breakdown lifted from the ledger row's metadata. */
export const creditTransactionDetailSchema = z.object({
  model: z.string().nullable().optional(),
  modelKey: z.string().optional(),
  queryType: z.string().optional(),
  inputTokens: z.number().int().optional(),
  outputTokens: z.number().int().optional(),
  costPaise: z.number().int().optional(),
  surface: z.string().optional(),
  sendKind: z.string().optional(),
  templateKey: z.string().optional(),
});

export const creditTransactionSchema = z.object({
  id: z.string().uuid(),
  wallet: creditWalletSchema,
  kind: creditEntryKindSchema,
  /** Signed credits: negative = spent, positive = granted. */
  credits: z.number().int(),
  description: z.string(),
  createdAt: z.string().datetime(),
  detail: creditTransactionDetailSchema.nullable(),
});

export const creditTransactionsInput = z.object({
  wallet: creditWalletSchema,
  cursor: z.string().uuid().nullable().default(null),
  limit: z.number().int().min(1).max(50).default(20),
});

export const creditTransactionsOutput = z.object({
  rows: z.array(creditTransactionSchema),
  nextCursor: z.string().uuid().nullable(),
});
